/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
  Activity, AlertTriangle, BadgeCheck, Banknote, BarChart3, Calendar,
  ClipboardCheck, Download, ExternalLink, Loader2, Package, RefreshCw, Sparkles,
  Stethoscope, TrendingDown, TrendingUp, User2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const previousMonthValue = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MODULE_META = {
  discipline:  { label: 'Discipline',          icon: BadgeCheck,    accent: 'text-indigo-600',  bg: 'bg-indigo-50',  border: 'border-indigo-200' },
  ventes:      { label: 'Score commercial',    icon: Banknote,      accent: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  receptions:  { label: 'Réceptions SADAGAS',  icon: Package,       accent: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  inventaires: { label: 'Inventaires',         icon: ClipboardCheck, accent: 'text-sky-700',    bg: 'bg-sky-50',     border: 'border-sky-200' },
  diagnostics: { label: 'Conversion diagnostics → ventes', icon: Sparkles, accent: 'text-fuchsia-700', bg: 'bg-fuchsia-50', border: 'border-fuchsia-200' },
  patients:    { label: 'Fidélisation patients', icon: Stethoscope, accent: 'text-rose-700',   bg: 'bg-rose-50',    border: 'border-rose-200' },
};

const STATUS_META = {
  performant:    { label: 'Performant',     bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  a_suivre:      { label: 'À suivre',       bg: 'bg-amber-100',   text: 'text-amber-800',   border: 'border-amber-300' },
  a_accompagner: { label: 'À accompagner',  bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300' },
  non_evalue:    { label: 'Non évalué',     bg: 'bg-zinc-100',    text: 'text-zinc-600',    border: 'border-zinc-300' },
};

// Libellés métier français des indicateurs détaillés (clés `details` renvoyées
// par le backend). Doit rester synchronisé avec `DETAIL_LABELS` dans
// `backend/services/agent_performance/pdf_export.py`.
const DETAIL_LABELS = {
  // Discipline
  ponctualite_score: 'Score ponctualité',
  ecart_caisse_pct: "Taux d'écart caisse",
  jours_evalues: 'Jours évalués',
  retards_count: 'Nombre de retards',
  retards_minutes_total: 'Minutes de retard cumulées',
  ecarts_caisse_jours: 'Jours avec écart caisse',
  ecarts_caisse_total_fcfa: 'Écart caisse total (FCFA)',
  // Diagnostics
  diagnostics_total: 'Diagnostics réalisés',
  diagnostics_simples: 'Diagnostics simples',
  diagnostics_complets: 'Diagnostics complets',
  diagnostics_convertis: 'Diagnostics convertis en vente',
  taux_conversion_pct: 'Taux de conversion (%)',
  // Patients (fidélisation)
  patients_suivis_distincts: 'Patients suivis (distincts)',
  patients_fidelises_3plus: 'Patients fidélisés (≥3 mesures)',
  mesures_total: 'Mesures réalisées',
  taux_fidelisation_pct: 'Taux de fidélisation (%)',
  // Inventaires
  inventaires_total: 'Inventaires affectés',
  inventaires_termines: 'Inventaires terminés',
  ecarts_fcfa_cumules: 'Écarts cumulés (FCFA)',
  ecarts_score: 'Sous-score écarts',
  actions_a_faire_count: 'Actions à corriger',
  actions_en_retard_count: 'Actions en retard',
  retards_score: 'Sous-score retards',
  delai_moyen_correction_h: 'Délai moyen de correction (h)',
  delai_score: 'Sous-score délai',
  // Réceptions
  receptions_saisies: 'Réceptions saisies',
  receptions_validees: 'Réceptions validées',
  total_count: 'Total réceptions',
  objectif_mensuel_count: 'Objectif mensuel',
  // Ventes / commercial
  atteinte_ca: 'Atteinte CA',
  atteinte_nb_ventes: 'Atteinte nb ventes',
  ventes_count: 'Nombre de ventes',
  ca_total_fcfa: 'CA total (FCFA)',
  ca_objectif_mensuel_fcfa: 'Objectif CA mensuel (FCFA)',
};

const humanizeDetailKey = (k) => DETAIL_LABELS[k] || k.replace(/_/g, ' ');

const scoreColor = (s) => {
  if (s == null) return 'bg-zinc-100 text-zinc-500 border-zinc-200';
  if (s >= 85) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
  if (s >= 70) return 'bg-orange-100 text-[#C45C26] border-orange-300';
  return 'bg-red-100 text-red-700 border-red-300';
};

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('fr-FR') : n ?? '—');

const ModuleCard = ({ moduleKey, mod, weight, extraSlot }) => {
  const meta = MODULE_META[moduleKey];
  const Icon = meta?.icon || Activity;
  if (!mod || mod.score == null) {
    const reason = mod?.warning || 'Module non exercé sur la période — exclu du score global (poids renormalisé).';
    return (
      <Card className="border-dashed border-zinc-300 bg-zinc-50/50" data-testid={`report-module-${moduleKey}-inactive`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-zinc-400">
            <Icon className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wide">{meta?.label}</span>
          </div>
          <p className="text-xs text-zinc-500 mt-2 italic leading-snug">{reason}</p>
          {extraSlot}
        </CardContent>
      </Card>
    );
  }
  const d = mod.details || {};
  return (
    <Card className={`${meta.border} border-2`} data-testid={`report-module-${moduleKey}`}>
      <CardContent className={`p-4 ${meta.bg}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${meta.accent}`} />
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-700">{meta.label}</span>
          </div>
          {weight != null && (
            <span className="text-[10px] text-zinc-500 font-mono">poids&nbsp;{weight}%</span>
          )}
        </div>
        <div className="flex items-end gap-2 mt-2">
          <div className="text-3xl font-mono font-bold text-zinc-900">{mod.score}</div>
          <div className="text-xs text-zinc-500 mb-1">/100</div>
          <Badge className={`${scoreColor(mod.score)} border ml-auto font-mono text-xs`}>
            {mod.score >= 85 ? 'Excellent' : mod.score >= 70 ? 'Bon' : mod.score >= 50 ? 'À renforcer' : 'Insuffisant'}
          </Badge>
        </div>
        {mod.warning && (
          <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-snug">
            ⚠️ {mod.warning}
          </div>
        )}
        <ul className="mt-3 space-y-0.5 text-xs text-zinc-600">
          {Object.entries(d).map(([k, v]) => (
            <li key={k} className="flex justify-between gap-2">
              <span className="text-zinc-500">{humanizeDetailKey(k)}</span>
              <span className="font-mono tabular-nums text-zinc-800 text-right">{fmt(v)}</span>
            </li>
          ))}
        </ul>
        {extraSlot}
      </CardContent>
    </Card>
  );
};

const InsightList = ({ icon: Icon, title, items, tone }) => {
  if (!items || items.length === 0) return null;
  const toneClasses = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    danger:  'border-red-300 bg-red-50 text-red-800',
  };
  return (
    <div className={`rounded-lg border-2 p-4 ${toneClasses[tone]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      <ul className="space-y-1 text-sm">
        {items.map((t, i) => <li key={i} className="leading-snug">• {t}</li>)}
      </ul>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.non_evalue;
  return (
    <Badge className={`${m.bg} ${m.text} ${m.border} border font-medium text-xs`} data-testid={`status-${status}`}>
      {m.label}
    </Badge>
  );
};

const RapportIndividuel = ({ canSelectAgent, currentUserId }) => {
  const defaultPeriod = useMemo(previousMonthValue, []);
  const [period, setPeriod] = useState(defaultPeriod);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(currentUserId || null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canSelectAgent) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-performance/agents-list`, { headers: auth() });
        setAgents(r.data || []);
        if (!agentId && r.data?.length) {
          const me = r.data.find((a) => a.id === currentUserId);
          const firstAgent = r.data.find((a) => a.role === 'agent');
          setAgentId((me || firstAgent || r.data[0]).id);
        }
      } catch (e) { toast.error('Erreur chargement liste agents'); }
    })();
  }, [canSelectAgent]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReport = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const url = canSelectAgent && agentId
        ? `${API}/agent-performance/report/${agentId}?period=${period}`
        : `${API}/agent-performance/report/me?period=${period}`;
      const r = await axios.get(url, { headers: auth() });
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement rapport');
      setReport(null);
    } finally { setLoading(false); }
  }, [period, agentId, canSelectAgent]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportPdf = async () => {
    if (!period) return;
    try {
      const url = canSelectAgent && agentId
        ? `${API}/agent-performance/report-pdf/${agentId}?period=${period}`
        : `${API}/agent-performance/report-pdf/me?period=${period}`;
      const r = await axios.get(url, { headers: auth(), responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = (report?.agent?.name || 'agent').replace(/\s+/g, '_');
      link.download = `Bilan_${safeName}_${period}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur export PDF');
    }
  };

  const globalScore = report?.global?.global_score;
  const weightsApplied = report?.global?.weights_applied || {};
  const inactives = report?.global?.modules_inactifs || [];

  return (
    <div className="space-y-5" data-testid="rapport-individuel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-serif font-semibold text-zinc-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C45C26]" /> Rapport individuel · Admin RH
          </h3>
          <p className="text-xs text-zinc-500">
            Synthèse multi-modules · Score global adaptatif · Mois précédent par défaut · Détails commerciaux dans
            <span className="font-medium text-zinc-700"> Admin SkinCare</span>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSelectAgent && (
            <select
              value={agentId || ''}
              onChange={(e) => setAgentId(e.target.value)}
              className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white"
              data-testid="report-agent-select"
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.role !== 'agent' ? `(${a.role})` : ''}
                </option>
              ))}
            </select>
          )}
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm"
            data-testid="report-period-input"
          />
          <Button variant="outline" size="sm" onClick={loadReport} disabled={loading} data-testid="report-reload-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recharger
          </Button>
          <Button
            size="sm"
            onClick={exportPdf}
            disabled={loading || !report}
            className="bg-[#1B4D3E] hover:bg-[#0F3D32] text-white"
            data-testid="report-export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Exporter PDF
          </Button>
        </div>
      </div>

      {loading && !report && (
        <div className="text-center py-10 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Chargement…
        </div>
      )}

      {!loading && !report && (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-sm text-zinc-500">Aucun rapport disponible.</CardContent>
        </Card>
      )}

      {report && (
        <>
          {report.excluded_from_ranking && (
            <div className="rounded-lg border-2 border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-700">
              ℹ️ Cet agent n'a aucune activité enregistrée sur la période — il est <strong>exclu du classement RH</strong>.
            </div>
          )}

          {/* Global Score Card */}
          <Card className="bg-gradient-to-br from-[#1B4D3E] to-[#0F3D32] text-white border-0" data-testid="report-global-card">
            <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase tracking-wider">
                  <User2 className="w-4 h-4" /> {report.agent?.name || 'Agent'}
                </div>
                <p className="text-2xl font-serif font-bold mt-1">Score global · {report.period}</p>
                <p className="text-sm text-emerald-100 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {report.date_from} → {report.date_to}
                </p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <StatusBadge status={report.status} />
                  {report.partial_evaluation && (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid="partial-eval-badge">
                      Évaluation partielle ({report.modules_metier_actifs_count || 0}/5 modules métier)
                    </Badge>
                  )}
                  {report.discipline_capped && (
                    <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-300 text-[10px]">
                      Discipline plafonnée à 25%
                    </Badge>
                  )}
                  {inactives.length > 0 && (
                    <span className="text-xs text-emerald-200/80 italic">
                      {inactives.length} module(s) inactif(s)
                    </span>
                  )}
                </div>
                {report.status_reason && (
                  <p className="text-[11px] text-amber-200 mt-1.5 italic">
                    ⚠ {report.status_reason}
                  </p>
                )}
                {report.improvement_message && (
                  <p className="text-sm text-white/90 mt-2 italic">💡 {report.improvement_message}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-6xl font-mono font-bold leading-none">{globalScore ?? '—'}</div>
                <div className="text-sm text-emerald-100 mt-1">/100</div>
              </div>
            </CardContent>
          </Card>

          {/* Modules Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.keys(MODULE_META).map((k) => (
              <ModuleCard
                key={k}
                moduleKey={k}
                mod={report.modules?.[k]}
                weight={weightsApplied[k]}
                extraSlot={
                  k === 'ventes' ? (
                    <div className="mt-3 pt-2 border-t border-emerald-200/60">
                      <a
                        href="#admin-skincare"
                        onClick={(e) => { e.preventDefault(); toast.info('Onglet « Admin SkinCare » → Stats agents → Définir l\'objectif'); }}
                        className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline"
                        data-testid="link-admin-skincare"
                      >
                        <ExternalLink className="w-3 h-3" /> Détails commerciaux dans Admin SkinCare
                      </a>
                    </div>
                  ) : null
                }
              />
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <InsightList icon={TrendingUp}   title="Points forts"  items={report.insights?.forces}     tone="success" />
            <InsightList icon={TrendingDown} title="À renforcer"   items={report.insights?.faiblesses} tone="warning" />
            <InsightList icon={AlertTriangle} title="Alertes"       items={report.insights?.alertes}    tone="danger" />
          </div>

          <p className="text-[10px] text-zinc-400 text-center">
            Pondération appliquée : {Object.entries(weightsApplied).map(([k, v]) => `${MODULE_META[k]?.label || k} ${v}%`).join(' · ') || 'N/A'}
            {' '}· Généré le {(report.generated_at || '').slice(0, 16).replace('T', ' ')}
          </p>
        </>
      )}
    </div>
  );
};

export default RapportIndividuel;
